import {customElement, property, queryAll} from "lit/decorators.js";
import {FormInputElement} from "./formInputElement";
import {html, type PropertyValues, type TemplateResult} from "lit";
import type {EopCheckbox} from "../../../page/elements/input/checkbox";
import type {Maybe} from "../../../bootstrap/common/types";
import {Dictionary} from "../../../page/elements/dictionary";
import {CHECKBOX_EVENT, type CheckboxEvent} from "../../../page/elements/input/inputEvents";
import type {CheckboxOption} from "../formConfig";
import {unsafeHTML} from "lit/directives/unsafe-html.js";

@customElement("eop-form-checkbox-group")
export class EopFormCheckboxGroup extends FormInputElement<boolean[]> {

    @property({attribute: "options", type: Array})
    public options: CheckboxOption[] = [];
    @property({attribute: "label"})
    public label: string;
    @queryAll("eop-checkbox")
    private checkboxes: NodeListOf<EopCheckbox>;

    private dictionary: Dictionary;

    public constructor() {
        super([false]);
    }

    public connectedCallback(): void {
        this.dictionary = Dictionary.of(this);
        super.connectedCallback();
    }

    public render(): TemplateResult {
        return html`
            <div class="input-element ${this.basicClassMap()}">
                <div class="top-label">
                    <span class="top-label-text">${this.label}</span>
                </div>
                <div class="checkboxes">
                    ${this.renderCheckboxes()}
                </div>
            </div>
            <eop-form-validation-messages
                    .validationFailures=${this.validationFailures}
                    .isInErrorState=${this.isInErrorState()}
                    .isInWarningState=${this.isInWarningState()}>
            </eop-form-validation-messages>
        `;
    }

    public focusInput(): void {
        this.checkboxes.item(0)?.focusInput();
    }

    private renderCheckboxes(): TemplateResult[] {
        return this.options.map((option, index) => {
            return html`
                <eop-checkbox
                        .inputId=${this.fullIdForOption(option)}
                        .checked=${this.isCheckedAt(index)}
                ></eop-checkbox>
                <label for=${this.fullIdForOption(option)}>
                    <span class="label-text">${unsafeHTML(option.label)}</span>
                </label>
            `;
        });
    }

    private isCheckedAt(index: number): boolean {
        if (!this.value) {
            return false;
        }
        return this.value[index] ?? false;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this.addEventListener(CHECKBOX_EVENT, (e: Event) => {
            e.stopPropagation();
            this.toggle(e);
        });
    }

    private toggle(event: Event): void {
        const inputEvent = event as CheckboxEvent<boolean>;
        for (let i = 0; i < this.options.length; i++) {
            const option = this.options[i];
            if (this.fullIdForOption(option) === inputEvent.detail.inputId) {
                const newValue = this.value.slice();
                newValue[i] = inputEvent.detail.inputValue;
                this.updateValue(newValue);
                return;
            }
        }
    }

    private fullIdForOption(option: CheckboxOption): string {
        return this.getFullId(option.id);
    }

    private checkboxesToValues(): CheckboxValue[] {
        const values: CheckboxValue[] = [];
        for (let i = 0; i < this.options.length; i++) {
            const option = this.options[i];
            const checked = this.isCheckedAt(i);
            let label = checked
                ? option.checkedLabel
                : option.uncheckedLabel;
            label = label || this.dictionary.translate("MSG_CHECKED", checked.toString()) + ": " + option.label;
            values.push({id: option.id, checked: checked, label: label});
        }
        return values;
    }

    protected preset(): Maybe<boolean[]> {
        const values: boolean[] = [];
        for (const checkbox of this.options) {
            values.push(checkbox.preselected ? checkbox.preselected : false);
        }
        return values;
    }

    protected valueEnhancements(_inputValue: boolean[]): Map<string, string> {
        const map = new Map<string, string>();
        const checkboxValues = this.checkboxesToValues();
        map.set("label", this.renderInBrackets(checkboxValues.map(v => v.label)));
        map.set("checked_labels", this.renderInBrackets(checkboxValues.filter(v => v.checked).map(v => v.label)));
        map.set("unchecked_labels", this.renderInBrackets(checkboxValues.filter(v => !v.checked).map(v => v.label)));
        map.set("checked_ids", JSON.stringify(checkboxValues.filter(v => v.checked).map(v => v.id)));
        return map;
    }

    private renderInBrackets(values: string[]): string {
        return "[" + values.join("],\n[") + "]";
    }
}

export type CheckboxValue = {
    id: string;
    checked: boolean;
    label: string;
};